import {
	getNlqV3Result,
	GetNlqV3ResultRequest,
	QueryExecutionResult,
	QueryState,
} from '@/services/nlq/nlq-service';
import { HttpClient } from '@sisense/sdk-rest-client';

const MAX_LAYER2_PRIOR_JSON_CHARS = 6000;

/** Matches the wire closing delimiter; must not appear literally inside the JSON payload. */
const PRIOR_NLQ_STATE_CLOSING_IN_PAYLOAD = /\[\/prior_nlq_state\]/g;

function escapePriorNlqClosingSentinels(serialized: string): string {
	return serialized.replace(
		PRIOR_NLQ_STATE_CLOSING_IN_PAYLOAD,
		'[\u200c/prior_nlq_state]',
	);
}

/**
 * Produces JSON at most `maxChars` long: full {@link QueryState}, or a wrapper with an excerpt
 * when the full serialization exceeds the budget.
 */
function serializePriorStateForNlqLayer2(
	priorState: QueryState,
	maxChars: number,
): { serialized: string; truncated: boolean } {
	const full = JSON.stringify(priorState);
	if (full.length <= maxChars) {
		return { serialized: full, truncated: false };
	}

	let excerptLen = Math.max(0, maxChars - 120);
	while (excerptLen > 0) {
		const wrapped = JSON.stringify({
			__nlq_prior_truncated: true,
			__nlq_prior_excerpt: full.slice(0, excerptLen),
		});
		if (wrapped.length <= maxChars) {
			return { serialized: wrapped, truncated: true };
		}
		excerptLen = Math.floor(excerptLen * 0.85);
	}

	return {
		serialized: JSON.stringify({ __nlq_prior_truncated: true }),
		truncated: true,
	};
}

/**
 * Layer-2 NLQ fallback when wire `chartState` is disabled (`isNlqDeltaEnabled === false`):
 * prepend a machine-readable JSON snapshot of the prior structural state, then the user's
 * prompt. NLQ receives a single `text` field (no `chartState`). Truncate very large states
 * to cap token growth; truncation is logged.
 */
export function augmentNlqTextWithPriorState(
	userText: string,
	priorState: QueryState,
): { text: string; truncated: boolean } {
	const { serialized: raw, truncated } = serializePriorStateForNlqLayer2(
		priorState,
		MAX_LAYER2_PRIOR_JSON_CHARS,
	);
	const serialized = escapePriorNlqClosingSentinels(raw);
	const block =
		'[prior_nlq_state json]\n' + serialized + '\n[/prior_nlq_state]\n\n';
	return { text: block + userText, truncated };
}

/** Call NLQ V3 smartQuery API; maps wire `chartState` to domain `queryState`. */
export async function executeNlqV3Query(
	dataSourceTitle: string,
	queryPrompt: string,
	httpClient?: HttpClient,
	commonFilters?: unknown,
	priorQueryState?: QueryState,
	isNlqDeltaEnabled = true,
): Promise<QueryExecutionResult | null> {
	const request: GetNlqV3ResultRequest = {
		text: queryPrompt,
		forceGeneration: true,
	};
	if (commonFilters != null) {
		request.commonFilters = commonFilters;
	}
	if (priorQueryState != null) {
		if (isNlqDeltaEnabled) {
			request.chartState = priorQueryState;
		} else {
			const { text } = augmentNlqTextWithPriorState(
				queryPrompt,
				priorQueryState,
			);
			request.text = text;
		}
	}
	const result = await getNlqV3Result(dataSourceTitle, request, httpClient);
	if (!result) {
		return null;
	}
	const { chartState, ...rest } = result;
	return { queryState: chartState, ...rest };
}
